import { useEffect, useState, type RefObject } from "react";

/**
 * True once `ref` is within `margin` px of the viewport (and stays true).
 *
 * For images that `loading="lazy"` does not hold back: Chrome's lazy-load
 * distance threshold is one to several screens depending on the connection, so
 * on a page whose sections are all server-rendered every below-the-fold card
 * image starts downloading at parse time — on the homepage that was ~19 MB in
 * flight alongside the hero image, which is the LCP. Cards that opt in render
 * their fixed-aspect box on the server and mount the <img> only when this
 * returns true. Without JavaScript the caller's <noscript> fallback shows the
 * image. Before hydration and without IntersectionObserver this stays false
 * until the effect runs, then flips to true immediately when the API is missing.
 */
export function useNearViewport(ref: RefObject<Element | null>, margin = 600): boolean {
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: `${margin}px 0px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, margin, near]);
  return near;
}
